import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../utils/date';

const InterviewIntentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [intents, setIntents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading || !user?.id || user.role !== 'EMPLOYER') return;
    apiService.interviewIntents.getByEmployer(user.id)
      .then((res: any) => {
        if (res.success && res.data) setIntents(res.data);
      })
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  if (!authLoading && (!user || user.role !== 'EMPLOYER')) {
    return <Navigate to="/home" replace />;
  }

  if (authLoading || loading) {
    return (
      <div className="flex min-h-[calc(100vh-64px)] items-center justify-center">
        <Loading message={t('common.loading', '載入中...')} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />
        {t('common.back', '返回')}
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('interviewIntents.title', '面試意向')}（{intents.length}）</CardTitle>
          <p className="text-sm text-muted-foreground">{t('interviewIntents.subtitle', '已發送的面試意向及外傭回覆狀態（7天內有效）')}</p>
        </CardHeader>
        <CardContent>
          {intents.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
              {t('interviewIntents.noIntents', '尚未發送任何面試意向。')}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-border/70">
              <div className="min-w-[720px]">
                <div className="grid grid-cols-[1.4fr_0.7fr_0.7fr_1.1fr_1.1fr] bg-muted/40 px-4 py-3 text-xs font-semibold text-muted-foreground">
                  <span>{t('interviewIntents.helper', '外傭')}</span>
                  <span className="text-center">{t('interviewIntents.replied', '是否已回覆')}</span>
                  <span className="text-center">{t('interviewIntents.replyContent', '回覆內容')}</span>
                  <span>{t('interviewIntents.sentDate', '發出日期')}</span>
                  <span>{t('interviewIntents.lastUpdated', '最後更新時間')}</span>
                </div>
                {intents.map((intent: any) => {
                  const replied = intent.status !== 'PENDING';
                  return (
                    <div
                      key={intent.id}
                      className="grid grid-cols-[1.4fr_0.7fr_0.7fr_1.1fr_1.1fr] items-center gap-2 border-t border-border/60 px-4 py-3 text-sm cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => intent.helperProfileId && navigate(`/helper/${intent.helperProfileId}`)}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarImage src={intent.helperAvatarUrl} alt={intent.helperName} />
                          <AvatarFallback>{intent.helperName?.[0] ?? '?'}</AvatarFallback>
                        </Avatar>
                        <span className="truncate font-medium text-foreground">{intent.helperName || '—'}</span>
                      </span>
                      <span className={`text-center font-medium ${replied ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {intent.status === 'EXPIRED' ? t('interviewIntents.expired', '已過期') : replied ? t('common.yes', '是') : t('common.no', '否')}
                      </span>
                      <span className={`text-center font-medium ${intent.status === 'ACCEPTED' ? 'text-green-600' : intent.status === 'EXPIRED' ? 'text-muted-foreground' : replied ? 'text-red-500' : 'text-muted-foreground'}`}>
                        {intent.status === 'EXPIRED' ? '—' : replied ? (intent.status === 'ACCEPTED' ? t('common.yes', '是') : t('common.no', '否')) : '—'}
                      </span>
                      <span className="text-muted-foreground">{formatDateTime(intent.createdAt)}</span>
                      <span className="text-muted-foreground">{formatDateTime(intent.updatedAt || intent.respondedAt)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InterviewIntentsPage;
